import { IHTVPlatform } from './platform'
import { LocalChannel } from '../interfaces/local-channel'
import { Injectable } from '@angular/core'
import { ChannelPosition } from '../pages/channel/channel-position'
import { MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { LocalMediaFile, Media } from 'htv-lib'
import { LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { ChannelTrackData, CurrentChannelData } from '../interfaces/channel-track-data'
import { PowerState } from '../interfaces/power-state'
import { BehaviorSubject } from 'rxjs'
import { LogService } from 'htv-lib'
import { NetworkData, NetworkState } from '../interfaces/network'
import { StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { RouterService, RouterPage } from '../services/router.service'
import { environment } from '../environments/environment'
import { HttpUtil } from '../utils/http-util'
import { GoogleCastState } from '../interfaces/local-googlecast'
import { ExternalInputData, ExternalInputType } from '../services/external-input.service'
import { DVBTChannel, UDPChannel, WebChannel } from '../interfaces/channel'
import { VODVideo } from '../interfaces/vod'

declare global {
  interface Window {
    AndroidInterface: {
      showToast: (message: string) => void
      getNetworkData: () => string
      getNetworkState: () => string
      getSystemData: () => string
      getPowerState: () => string
      startApp: (appId: string) => void
      getAppList: () => string
      downloadAndUpgradeApp: (humecloudBaseUrl: string, htvAuth: string) => void
      getLocalMedias: () => string
      downloadMedia: (filename: string, path: string) => string
      startUDPChannel: (url: string, posLeft: number, posTop: number, posWidth: number, posHeight: number) => void
      stopUDPChannel: () => void
      startWebChannel: (url: string, posLeft: number, posTop: number, posWidth: number, posHeight: number) => void
      stopWebChannel: () => void
      startBroadcastChannel: (
        frequency: number,
        bandwidth: number,
        programNumber: number,
        serviceID: number,
        dvbt2: boolean,
        posLeft: number,
        posTop: number,
        posWidth: number,
        posHeight: number
      ) => void
      stopBroadcastChannel: () => void
      setVideoOnTop: (onTop: boolean) => void
      getCurrentProgramData: () => string
      getWebCurrentProgramData: () => string
      setSubtitleTrack: (index: number) => void
      setAudioTrack: (index: number) => void
      setWebSubtitleTrack: (index: number) => void
      setWebAudioTrack: (index: number) => void
      setSubtitleState: (enable: boolean) => boolean
      setWebSubtitleState: (enable: boolean) => boolean
      getCurrentChannelTunedDetails: () => boolean
      getVolume: () => string
      setVolume: (volume: number) => string
      getVolumeMute: () => string
      setVolumeMute: (muted: boolean) => string
      initGoogleCastApp: () => string
      getWifiDirectState: () => string
      startWifiDirect: () => string
      clearGoogleCastData: () => void
      hasIntegratedGoogleCast: () => string
      getExternalInputs: () => string
      getExternalInputsState: () => string
      showHDMI: (inputId: string) => void
      hideHDMI: () => void
      initVirtualKeyboard: () => void
      setNativeVolumeDisplay: (enabled: boolean) => void
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class PhilipsPlatform implements IHTVPlatform {
  /*
  audioTracks: ChannelTrackData[] = [
    { index: 0, lang: 'fr', data: {}, flags: [] },
    { index: 1, lang: 'en', data: {}, flags: [] },
  ]
  subtitleTracks: ChannelTrackData[] = [
    { index: 3, lang: 'fr', data: {}, flags: [] },
    { index: 4, lang: 'fr', data: {}, flags: [ChannelTrackDataFlags.HEARING_IMPAIRED] },
    { index: 5, lang: 'en', data: {}, flags: [] },
    { index: 6, lang: 'it', data: {}, flags: [] },
    { index: 7, lang: 'de', data: {}, flags: [ChannelTrackDataFlags.HEARING_IMPAIRED] },
    { index: 8, lang: 'es', data: {}, flags: [] },
  ]
  isSubtitleEnabled = false
  currentAudioTrack: ChannelTrackData = this.audioTracks[0]
  currentSubtitleTrack: ChannelTrackData = this.subtitleTracks[0]
  */

  private readonly MEDIA_DIR = '/content/medias'
  private webChannelActive = false

  muted = false
  volume = 10

  powerState?: PowerState
  pendingPowerState?: PowerState
  powerStateTimer: any
  readonly powerStateTimerDelayInMs = 1000

  gcInitializedSubject?: BehaviorSubject<boolean | undefined>
  channelChangedSubject?: BehaviorSubject<boolean>

  constructor(
    private logService: LogService,
    private routerService: RouterService
  ) {
    ;(window as any).interfaceMethods = {
      onBackKey: this.onBackKey.bind(this),
      onHomeKey: this.onHomeKey.bind(this),
    }
    this.logService.debug('PhilipsPlatform loaded')
  }

  id(): string {
    return 'PhilipsPlatform'
  }

  init(): Promise<void> {
    this.generateMetaViewport()
    return Promise.resolve()
  }
  generateMetaViewport() {
    let metaViewportElement = document.querySelector('meta[name="viewport"]')
    if (metaViewportElement === null) {
      metaViewportElement = document.createElement('meta')
      metaViewportElement.setAttribute('name', 'viewport')
      document.querySelector('head')?.appendChild(metaViewportElement)
    }
    const metaViewportContent =
      'user-scalable=no, width=device-width, initial-scale=' + 1 / devicePixelRatio + ', maximum-scale=1'
    metaViewportElement.setAttribute('content', metaViewportContent)
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterRegistrationHook(deviceId: string | undefined): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterStatusHook(status: StatusSuccessDto): Promise<void> {
    return Promise.resolve()
  }

  enableGuestMode(): Promise<void> {
    // todo : KEEP all app data on turn on
    return Promise.resolve()
  }

  disableGuestMode(): Promise<void> {
    // todo : DELETE all app data (except HumeIPTV) on turn on
    return Promise.resolve()
  }

  clearData(): Promise<void> {
    return Promise.resolve()
  }

  getSystemData(): Promise<SystemData> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getSystemData()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          this.logService.log(valueReturned)
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  getPlatformData(): Promise<object> {
    return Promise.resolve({
      userAgent: navigator.userAgent,
    })
  }

  getCpuUsage(): Promise<number> {
    return Promise.resolve(0)
  }

  getMemoryData(): Promise<MonitoringMemoryData> {
    return Promise.resolve({
      availableMemory: 0,
      totalMemory: 0,
    })
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return Promise.resolve([])
  }

  getTemperature(): Promise<number> {
    return Promise.resolve(0)
  }

  getValue(key: string): any {
    return localStorage.getItem(key)
  }

  setValue(key: string, value: any): void {
    localStorage.setItem(key, value)
  }

  removeValue(key: string): void {
    localStorage.removeItem(key)
  }

  clearValues(): void {
    localStorage.clear()
  }

  reboot(): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLanguage(code: string): Promise<void> {
    return Promise.resolve()
  }

  getPowerState(): Promise<PowerState | undefined> {
    return new Promise(resolve => {
      const valueReturned = window.AndroidInterface.getPowerState()
      if (valueReturned == null || valueReturned === 'UNKNOWN') {
        resolve(undefined)
      } else {
        const powerState = valueReturned === 'ON' ? PowerState.NORMAL : PowerState.STANDBY
        resolve(powerState)
      }
    })
  }

  setPowerState(state: PowerState): void {
    this.pendingPowerState = state
  }

  registerPowerStateListener(subject: BehaviorSubject<PowerState | undefined>): void {
    if (this.powerStateTimer) {
      clearInterval(this.powerStateTimer)
    }
    this.powerStateTimer = setInterval(async () => {
      const powerState = await this.getPowerState()
      if (this.powerState != powerState) {
        this.powerState = powerState
        subject.next(powerState)
      }
    }, this.powerStateTimerDelayInMs)
  }

  getNetworkState(): Promise<NetworkState> {
    try {
      return Promise.resolve(this.toNetworkState(window.AndroidInterface.getNetworkState()))
    } catch (err: any) {
      this.logService.error(`getNetworkState: ${err?.message}`)
      return Promise.resolve(NetworkState.UNKNOWN)
    }
  }

  getNetworkData(): Promise<NetworkData> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getNetworkData()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          this.logService.debug(valueReturned)
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  registerNetworkStateListener(subject: BehaviorSubject<NetworkState>): void {
    this.getNetworkState().then(state => subject.next(state))

    document.addEventListener(
      'philips::network_state_changed',
      (event: Event) => {
        const detail = (event as CustomEvent).detail
        subject.next(this.toNetworkState(detail))
      },
      false
    )
  }

  private toNetworkState(value: unknown): NetworkState {
    switch (value) {
      case 'CONNECTED':
        return NetworkState.CONNECTED
      case 'DISCONNECTED':
        return NetworkState.DISCONNECTED
      default:
        return NetworkState.UNKNOWN
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    // do nothing
  }

  getAppList(): Promise<TVApp[]> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getAppList()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          this.logService.log(valueReturned)
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  startApp(appId: string): void {
    try {
      window.AndroidInterface.startApp(appId)
    } catch (e) {
      this.logService.error('Failed to Start App: ' + appId)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startNetflixApp(appId: string, hotelId: string, launchMode: LocalAppLaunchMode): void {
    this.startApp(appId)
  }

  registerChannelListener(subject: BehaviorSubject<boolean>): void {
    this.channelChangedSubject = subject
    document.addEventListener(
      'philips::channel_changed',
      () => {
        subject.next(true)
      },
      false
    )
    //todo: move this to it's own event listener
    document.addEventListener(
      'philips::audio_changed',
      () => {
        subject.next(true)
      },
      false
    )

    //todo: move this to it's own event listener
    document.addEventListener(
      'philips::subtitle_changed',
      () => {
        subject.next(true)
      },
      false
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startBroadcastChannel(channel: LocalChannel, pos: ChannelPosition): void {
    const source = channel.source as DVBTChannel

    this.applyPlayerHole(pos)

    if (!source.freqHz) {
      this.logService.error('Invalid DVBT channel configuration: missing freqHz')
      return
    }
    if (!source.bandwidthMHz) {
      this.logService.error('Invalid DVBT channel configuration: missing bandwidth')
      return
    }
    try {
      window.AndroidInterface.startBroadcastChannel(
        source.freqHz,
        source.bandwidthMHz,
        source.channelNumber,
        source.serviceId,
        source.dvbt2,
        pos.left,
        pos.top,
        pos.width,
        pos.height
      )
    } catch (err: any) {
      this.logService.error(`Could not launch the channel. General error: ${err.message}`)
    }
  }

  private applyPlayerHole(pos: ChannelPosition): void {
    const { left, top, width, height } = pos
    const W = window.innerWidth
    const H = window.innerHeight

    const right = W - (left + width)
    const bottom = H - (top + height)

    const mask = `
      linear-gradient(white, white) top    / 100% ${top}px,
      linear-gradient(white, white) bottom / 100% ${bottom}px,
      linear-gradient(white, white) left   / ${left}px 100%,
      linear-gradient(white, white) right  / ${right}px 100%
    `.trim()

    const page = this.getPageRoot()
    if (page) {
      page.style.webkitMask = mask
      page.style.mask = mask
      page.style.webkitMaskRepeat = 'no-repeat'
      page.style.maskRepeat = 'no-repeat'
      return
    }
  }

  removePlayerHole(): void {
    const page = this.getPageRoot()

    if (page) {
      page.style.webkitMask = ''
      page.style.mask = ''
      page.style.webkitMaskRepeat = ''
      page.style.maskRepeat = ''
    }
  }

  private getPageRoot(): HTMLElement | null {
    const page = document.querySelector('[classname="page"]') as HTMLElement
    if (page) {
      this.logService.info('getPageRoot: found [classname=page]')
      return page
    }

    this.logService.info('getPageRoot: nothing found')
    return null
  }

  startUDPChannel(channel: LocalChannel, pos: ChannelPosition): void {
    const source = channel.source as UDPChannel

    this.applyPlayerHole(pos)

    if (!source.ip) {
      this.logService.error('Invalid UDP channel configuration: missing IP')
      return
    }
    if (!source.port) {
      this.logService.error('Invalid UDP channel configuration: missing port')
      return
    }
    try {
      //const protocol = source.rtp ? 'rtp' : 'udp'

      const protocol = 'multicast'
      const url = `${protocol}://${source.ip}:${source.port}`
      window.AndroidInterface.startUDPChannel(url, pos.left, pos.top, pos.width, pos.height)
    } catch (err: any) {
      this.logService.error(`Could not launch the channel. General error: ${err.message}`)
    }
  }

  stopUDPChannel(): void {
    window.AndroidInterface.stopUDPChannel()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startWebChannel(channel: LocalChannel, pos: ChannelPosition): void {
    const source = channel.source as WebChannel

    this.applyPlayerHole(pos)

    try {
      window.AndroidInterface.startWebChannel(source.url, pos.left, pos.top, pos.width, pos.height)
      this.webChannelActive = true
    } catch (err: any) {
      this.logService.error(`Could not launch Web channel. General error: ${err.message}`)
    }
  }

  stopWebChannel(): void {
    window.AndroidInterface.stopWebChannel()
    this.webChannelActive = false
  }

  stopBroadcastChannel(): void {
    window.AndroidInterface.stopBroadcastChannel()
  }

  getCurrentChannel(): Promise<any> {
    return new Promise(resolve => {
      let count = 0
      let cancelled = false
      const pollCurrentStreamInfo = () => {
        if (cancelled) return
        count++

        try {
          const state = window.AndroidInterface.getCurrentChannelTunedDetails()
          if (state) {
            cancelled = true
            return resolve(true)
          }
          //resolve(state)
        } catch (err: any) {
          // do nothing
        }

        if (count > 5) {
          cancelled = true
          return resolve(false)
        }
        setTimeout(pollCurrentStreamInfo, 500)
      }
      pollCurrentStreamInfo()
    })
  }

  getCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getCurrentProgramData()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  getUDPCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData = this.getCurrentProgramData()
    return Promise.resolve(channelData)
  }

  getWebCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getWebCurrentProgramData()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  getBroadcastCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData = this.getCurrentProgramData()
    return Promise.resolve(channelData)
  }

  setUDPAudioTrack(track: ChannelTrackData): void {
    this.setAudioTrack(track)
  }

  setWebAudioTrack(track: ChannelTrackData): void {
    window.AndroidInterface.setWebAudioTrack(track.index)
  }

  setBroadcastAudioTrack(track: ChannelTrackData): void {
    this.setAudioTrack(track)
  }

  setAudioTrack(track: ChannelTrackData): void {
    window.AndroidInterface.setAudioTrack(track.index)
  }

  setUDPSubtitleTrack(track: ChannelTrackData): void {
    this.setSubtitleTrack(track)
  }

  setWebSubtitleTrack(track: ChannelTrackData): void {
    window.AndroidInterface.setWebSubtitleTrack(track.index)
  }

  setBroadcastSubtitleTrack(track: ChannelTrackData): void {
    this.setSubtitleTrack(track)
  }

  setSubtitleTrack(track: ChannelTrackData): void {
    window.AndroidInterface.setSubtitleTrack(track.index)
  }

  setSubtitleState(enable: boolean): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const valueReturned = this.webChannelActive
        ? window.AndroidInterface.setWebSubtitleState(enable)
        : window.AndroidInterface.setSubtitleState(enable)
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(valueReturned)
        } catch (e) {
          reject()
        }
      }
    })
  }

  getLocalMedias(): Promise<LocalMediaFile[]> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getLocalMedias()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  onBackKey(): void {
    const event = new KeyboardEvent('keydown', {
      key: 'GoBack',
      code: 'GoBack',
      keyCode: 10009,
      which: 10009,
      bubbles: true,
      cancelable: true,
    })
    document.dispatchEvent(event)
  }

  onHomeKey(): void {
    this.routerService.goToHome()
  }

  downloadMedia(media: Media): Promise<LocalMediaFile> {
    const filename = `${media.id}.${media.fileExtension}`

    return new Promise((resolve, reject) => {
      const source = `${media.url}?ts=${new Date().getTime()}`
      const valueReturned = window.AndroidInterface.downloadMedia(source, filename)
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve({
            id: media.id,
            filename: filename,
            url: valueReturned,
            sizeInBytes: undefined,
            sha1: undefined,
            md5: undefined,
          })
        } catch (e) {
          reject()
        }
      }
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeMedia(mediaFile: LocalMediaFile): Promise<void> {
    return Promise.resolve()
  }

  setNativeVolumeDisplay(enabled: boolean): void {
    try {
      window.AndroidInterface.setNativeVolumeDisplay(enabled)
    } catch (e) {
      this.logService.error(`Could not set native volume display to ${enabled}`)
    }
  }

  getVolume(): Promise<number> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getVolume()

      if (valueReturned == null) {
        reject()
      } else {
        this.logService.debug('PhilipsPlatform get volume: ' + JSON.parse(valueReturned))
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  setVolume(volume: number): Promise<number> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.setVolume(volume)
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  async setVolumeDown(): Promise<number> {
    const volume = await this.getVolume()
    return this.setVolume(volume > 0 ? volume - 1 : 0)
  }

  async setVolumeUp(): Promise<number> {
    const volume = await this.getVolume()
    return this.setVolume(volume + 1)
  }

  getVolumeMute(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getVolumeMute()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.setVolumeMute(muted)
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return new Promise((resolve, reject) => {
      const valueReturned = window.AndroidInterface.getExternalInputs()
      if (valueReturned == null) {
        reject()
      } else {
        try {
          resolve(JSON.parse(valueReturned))
        } catch (e) {
          reject()
        }
      }
    })
  }

  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    document.addEventListener(
      'philips::external_input_changed',
      (param: any) => {
        const state: Record<string, boolean> = param?.detail ?? {}
        const externalInputs: ExternalInputData[] = Object.entries(state).map(([id, connected]) => ({
          id,
          type: ExternalInputType.HDMI,
          index: parseInt(id.replace('HDMI', ''), 10),
          number: parseInt(id.replace('HDMI', ''), 10),
          connected,
        }))
        subject.next(externalInputs)
      },
      false
    )
  }

  showHDMI(externalInput: ExternalInputData): Promise<void> {
    try {
      window.AndroidInterface.showHDMI(externalInput.id)
    } catch (err: any) {
      this.logService.error(`showHDMI: ${err?.message}`)
    }
    return Promise.resolve()
  }

  hideHDMI(): Promise<void> {
    try {
      window.AndroidInterface.hideHDMI()
    } catch (err: any) {
      this.logService.error(`hideHDMI: ${err?.message}`)
    }
    return Promise.resolve()
  }

  hasIntegratedGoogleCast(): boolean {
    try {
      return window.AndroidInterface.hasIntegratedGoogleCast() === 'true'
    } catch (err: any) {
      this.logService.error(`hasIntegratedGoogleCast: ${err?.message}`)
      return false
    }
  }

  initGoogleCastApp(): void {
    const result = window.AndroidInterface.initGoogleCastApp()
    const initialized = result === 'true'
    this.logService.debug(`PhilipsPlatform initGoogleCastApp: ${initialized}`)
    this.gcInitializedSubject?.next(initialized)
  }

  registerGoogleCastInitializedListener(subject: BehaviorSubject<boolean | undefined>): void {
    this.gcInitializedSubject = subject
  }

  disableGoogleCast(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastProxy(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastSoftAP(): Promise<void> {
    return Promise.resolve()
  }

  acceptGoogleCastTermsOfService(): Promise<void> {
    return Promise.resolve()
  }

  enableGoogleCastUsageReporting(): Promise<void> {
    return Promise.resolve()
  }

  clearGoogleCastData(): Promise<void> {
    try {
      window.AndroidInterface.clearGoogleCastData()
    } catch (err: any) {
      this.logService.error(`clearGoogleCastData: ${err?.message}`)
    }
    return Promise.resolve()
  }

  openGoogleCastLocalPage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    roomNumber: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    supportMessage: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    gcStateSubject: BehaviorSubject<GoogleCastState | undefined>
  ): Promise<void> {
    this.routerService.goToPage(RouterPage.CAST)
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  openGoogleCastSoftAPPage(roomNumber: string, pairingStatus: boolean, supportMessage: string): Promise<void> {
    this.routerService.goToPage(RouterPage.CAST)
    return Promise.resolve()
  }

  pollGoogleCastSoftAP(timeoutMs = 15000, intervalMs = 200): Promise<{ ssid: string; password: string } | undefined> {
    this.routerService.goToPage(RouterPage.CAST)
    return new Promise(resolve => {
      const deadline = Date.now() + timeoutMs

      const checkState = (): { ssid: string; password: string } | undefined => {
        try {
          const wifiDirectState = JSON.parse(window.AndroidInterface.getWifiDirectState())
          if (wifiDirectState?.started && wifiDirectState?.ssid && wifiDirectState?.password) {
            return { ssid: wifiDirectState.ssid, password: wifiDirectState.password }
          }
        } catch (err: any) {
          this.logService.error(`pollGoogleCastSoftAP: ${err?.message}`)
        }
        return undefined
      }

      const existingState = checkState()
      if (existingState) return resolve(existingState)

      if (window.AndroidInterface.startWifiDirect() !== 'true') {
        this.logService.error('startWifiDirect returned false')
        return resolve(undefined)
      }

      const pollWifiDirectState = () => {
        const result = checkState()
        if (result) return resolve(result)
        if (Date.now() > deadline) {
          this.logService.error('WifiDirect did not start within timeout')
          return resolve(undefined)
        }
        setTimeout(pollWifiDirectState, intervalMs)
      }
      setTimeout(pollWifiDirectState, intervalMs)
    })
  }

  launchGoogleCastSettings(): Promise<void> {
    return Promise.resolve()
  }

  adminOpenAppsSettings(): void {
    // do nothing
  }

  downloadAndUpgradeApp(deviceId: string): void {
    window.AndroidInterface.downloadAndUpgradeApp(environment.appUrl, HttpUtil.getHTVBasicAuth(deviceId))
  }

  canDownloadAndUpgradeApp(): boolean {
    return true
  }

  hasCustomVOD(): boolean {
    return false
  }

  getShakaPlatformConfig(): any {
    return undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodStart(vod: VODVideo, pos: ChannelPosition): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodPlay(vod: VODVideo): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodPause(vod: VODVideo): void {
    // do nothing
  }

  vodStop(): void {
    // do nothing
  }

  vodGetCurrentTime(): number {
    return 0
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodSpeed(vod: VODVideo, speed: number): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodSeekTo(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodJumpBackward(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodJumpForward(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  initVirtualKeyboard(): void {
    window.AndroidInterface.initVirtualKeyboard()
  }
}
